import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router-dom'
import { SETTINGS_KEYS, environment } from '../../config/environment'
import { useSetting } from '../../hooks/useSettings'
import { PermissionAction, PermissionType, permissionsService } from '../../services/permissions.service'
import { profileService } from '../../services/profile.service'
import { toastService } from '../../services/toast.service'
import { copyToClipboard } from '../../utils/clipboard'
import { useModuleFlag } from '../../utils/featureFlags'
import { ConfirmDialog } from '../common/ConfirmDialog'
import { PageHeader } from '../common/PageHeader'
import './ApiDocumentation.scss'

type ApiSection =
  | 'authentication'
  | 'organizations'
  | 'upload'
  | 'documents'
  | 'search'
  | 'bulk-edit'
  | 'tags'
  | 'correspondents'
  | 'document-types'
  | 'storage-paths'
  | 'consume-folder'

export const ApiDocumentation: React.FC = () => {
  const { t, i18n } = useTranslation()
  const [activeSection, setActiveSection] = useState<ApiSection>('authentication')
  const appTitle = useSetting(SETTINGS_KEYS.APP_TITLE, environment.appTitle)
  const apiBaseUrl = environment.apiBaseUrl
  const currentLanguage = i18n.language || 'sr'
  const isExternalIntegrationsModuleEnabled = useModuleFlag('external_integrations', true)

  const hasPermission = permissionsService.currentUserCan(PermissionAction.View, PermissionType.User)

  React.useEffect(() => {
    document.title = `${t('api_documentation')} - ${appTitle}`
  }, [t, appTitle])

  if (!hasPermission) {
    return <Navigate to="/dashboard" replace />
  }

  if (!isExternalIntegrationsModuleEnabled) {
    return <Navigate to="/dashboard" replace />
  }

  const sections: { id: ApiSection; title: string }[] = [
    { id: 'authentication', title: t('api_authentication') },
    { id: 'organizations', title: t('api_organizations') },
    { id: 'documents', title: t('api_documents') },
    { id: 'upload', title: t('api_upload_documents') },
    { id: 'search', title: t('api_search_documents') },
    { id: 'bulk-edit', title: t('api_bulk_edit') },
    { id: 'tags', title: t('api_tags') },
    { id: 'correspondents', title: t('api_correspondents') },
    { id: 'document-types', title: t('api_document_types') },
    { id: 'storage-paths', title: t('api_storage_paths') },
    { id: 'consume-folder', title: t('api_consume_folder') },
  ]

  return (
    <div className="api-documentation">
      <PageHeader title={t('api_documentation')} info={t('api_documentation_info')} />

      <div className="container-fluid">
        <div className="row">
          <div className="col-md-3">
            <div className="api-documentation__sidebar">
              <nav className="nav flex-column">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    className={`nav-link text-start ${activeSection === section.id ? 'active' : ''}`}
                    onClick={() => setActiveSection(section.id)}
                  >
                    {section.title}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          <div className="col-md-9">
            <div className="api-documentation__content">
              {activeSection === 'authentication' && (
                <AuthenticationDocumentation
                  apiBaseUrl={apiBaseUrl}
                  currentLanguage={currentLanguage}
                  externalIntegrationsEnabled={isExternalIntegrationsModuleEnabled}
                />
              )}
              {activeSection === 'organizations' && <OrganizationsDocumentation apiBaseUrl={apiBaseUrl} currentLanguage={currentLanguage} />}
              {activeSection === 'documents' && <DocumentsDocumentation apiBaseUrl={apiBaseUrl} currentLanguage={currentLanguage} />}
              {activeSection === 'upload' && <UploadDocumentation apiBaseUrl={apiBaseUrl} currentLanguage={currentLanguage} />}
              {activeSection === 'search' && <SearchDocumentation apiBaseUrl={apiBaseUrl} currentLanguage={currentLanguage} />}
              {activeSection === 'bulk-edit' && <BulkEditDocumentation apiBaseUrl={apiBaseUrl} currentLanguage={currentLanguage} />}
              {activeSection === 'tags' && <TagsDocumentation apiBaseUrl={apiBaseUrl} currentLanguage={currentLanguage} />}
              {activeSection === 'correspondents' && <CorrespondentsDocumentation apiBaseUrl={apiBaseUrl} currentLanguage={currentLanguage} />}
              {activeSection === 'document-types' && <DocumentTypesDocumentation apiBaseUrl={apiBaseUrl} currentLanguage={currentLanguage} />}
              {activeSection === 'storage-paths' && <StoragePathsDocumentation apiBaseUrl={apiBaseUrl} currentLanguage={currentLanguage} />}
              {activeSection === 'consume-folder' && <ConsumeFolderDocumentation apiBaseUrl={apiBaseUrl} currentLanguage={currentLanguage} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const UploadDocumentation: React.FC<{ apiBaseUrl: string; currentLanguage: string }> = ({ apiBaseUrl }) => {
  const { t } = useTranslation()

  return (
    <div>
      <h2>{t('api_upload_documents')}</h2>
      <p className="lead">{t('api_upload_description')}</p>

      <section className="mb-4">
        <h3>{t('api_endpoint')}</h3>
        <div className="code-block">
          <code>POST {apiBaseUrl}documents/post_document/</code>
        </div>
        <p>{t('api_upload_endpoint_info')}</p>
        <p>{t('api_upload_workflows_note')}</p>
      </section>

      <section className="mb-4">
        <h3>{t('api_authentication_required')}</h3>
        <p>{t('api_upload_auth_token_only')}</p>
      </section>

      <section className="mb-4">
        <h3>{t('api_request_format')}</h3>
        <p>{t('api_upload_request_format_info')}</p>
        <div className="code-block">
          <pre>{`Content-Type: multipart/form-data

${t('api_form_fields')}:
- document (file, ${t('required')}): ${t('api_field_document_desc')}
- title (string, ${t('optional')}): ${t('api_field_title_desc')}
- created (datetime, ${t('optional')}): ${t('api_field_created_desc')}
- correspondent (integer, ${t('optional')}): ${t('api_field_correspondent_desc')}
- document_type (integer, ${t('optional')}): ${t('api_field_document_type_desc')}
- storage_path (integer, ${t('optional')}): ${t('api_field_storage_path_desc')}
- tags (integer[], ${t('optional')}): ${t('api_field_tags_desc')}
- archive_serial_number (integer, ${t('optional')}): ${t('api_field_asn_desc')}
- custom_fields (object, ${t('optional')}): ${t('api_field_custom_fields_desc')}`}</pre>
        </div>
      </section>

      <section className="mb-4">
        <h3>{t('api_request_example')}</h3>
        <div className="code-block">
          <pre>{`curl -X POST "${apiBaseUrl}documents/post_document/" \\
  -H "Authorization: Token YOUR_API_TOKEN" \\
  -F "document=@/path/to/document.pdf" \\
  -F "title=${t('api_example_title')}" \\
  -F "correspondent=1" \\
  -F "tags=2" \\
  -F "tags=5"`}</pre>
        </div>
      </section>

      <section className="mb-4">
        <h3>{t('api_response')}</h3>
        <p>{t('api_upload_response_info')}</p>
        <div className="code-block">
          <pre>{`HTTP 200 OK
Content-Type: application/json

"550e8400-e29b-41d4-a716-446655440000"`}</pre>
        </div>
        <p>{t('api_upload_response_note')}</p>
      </section>

      <section className="mb-4">
        <h3>{t('api_checking_status')}</h3>
        <p>{t('api_upload_status_info')}</p>
        <div className="code-block">
          <pre>{`GET ${apiBaseUrl}tasks/?task_id=550e8400-e29b-41d4-a716-446655440000`}</pre>
        </div>
        <p>{t('api_upload_status_response')}</p>
        <div className="code-block">
          <pre>{`{
  "id": 123,
  "task_id": "550e8400-e29b-41d4-a716-446655440000",
  "status": "SUCCESS",
  "result": "Document 456 created successfully",
  "date_created": "2024-01-15T10:30:00Z",
  "date_done": "2024-01-15T10:31:15Z"
}`}</pre>
        </div>
      </section>

      <section className="mb-4">
        <h3>{t('api_field_details')}</h3>
        <div className="table-responsive">
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>{t('api_field_name')}</th>
                <th>{t('api_field_type')}</th>
                <th>{t('api_field_required')}</th>
                <th>{t('api_field_description')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <code>document</code>
                </td>
                <td>file</td>
                <td>{t('yes')}</td>
                <td>{t('api_field_document_desc')}</td>
              </tr>
              <tr>
                <td>
                  <code>title</code>
                </td>
                <td>string</td>
                <td>{t('no')}</td>
                <td>{t('api_field_title_desc')}</td>
              </tr>
              <tr>
                <td>
                  <code>created</code>
                </td>
                <td>datetime</td>
                <td>{t('no')}</td>
                <td>{t('api_field_created_desc')}</td>
              </tr>
              <tr>
                <td>
                  <code>correspondent</code>
                </td>
                <td>integer</td>
                <td>{t('no')}</td>
                <td>{t('api_field_correspondent_desc')}</td>
              </tr>
              <tr>
                <td>
                  <code>document_type</code>
                </td>
                <td>integer</td>
                <td>{t('no')}</td>
                <td>{t('api_field_document_type_desc')}</td>
              </tr>
              <tr>
                <td>
                  <code>storage_path</code>
                </td>
                <td>integer</td>
                <td>{t('no')}</td>
                <td>{t('api_field_storage_path_desc')}</td>
              </tr>
              <tr>
                <td>
                  <code>tags</code>
                </td>
                <td>integer[]</td>
                <td>{t('no')}</td>
                <td>{t('api_field_tags_desc')}</td>
              </tr>
              <tr>
                <td>
                  <code>archive_serial_number</code>
                </td>
                <td>integer</td>
                <td>{t('no')}</td>
                <td>{t('api_field_asn_desc')}</td>
              </tr>
              <tr>
                <td>
                  <code>custom_fields</code>
                </td>
                <td>object</td>
                <td>{t('no')}</td>
                <td>{t('api_field_custom_fields_desc')}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="mb-4">
        <h3>{t('api_permissions')}</h3>
        <p>{t('api_upload_permissions_info')}</p>
        <p>{t('api_upload_permissions_note')}</p>
        <div className="code-block">
          <pre>{`{
  "owner": 1,
  "set_permissions": {
    "view": {
      "users": [2, 3],
      "groups": [1]
    },
    "change": {
      "users": [2],
      "groups": []
    }
  }
}`}</pre>
        </div>
        <p>{t('api_upload_permissions_arrays_note')}</p>
        <p>{t('api_upload_permissions_requirement')}</p>
        <p>{t('api_upload_permissions_full_perms')}</p>
        <div className="code-block">
          <pre>{`GET ${apiBaseUrl}documents/123/?full_perms=true`}</pre>
        </div>
      </section>

      <section className="mb-4">
        <h3>{t('api_error_handling')}</h3>
        <p>{t('api_upload_error_info')}</p>
        <div className="code-block">
          <pre>{`HTTP 400 Bad Request
{
  "document": ["This field is required."]
}

HTTP 401 Unauthorized
{
  "detail": "Authentication credentials were not provided."
}

HTTP 403 Forbidden
{
  "detail": "You do not have permission to perform this action."
}`}</pre>
        </div>
      </section>

      <section className="mb-4">
        <h3>{t('api_important_notes')}</h3>
        <ul>
          <li>{t('api_upload_note_filename')}</li>
          <li>{t('api_upload_note_async')}</li>
          <li>{t('api_upload_note_task_id')}</li>
          <li>{t('api_upload_note_supported_formats')}</li>
        </ul>
      </section>
    </div>
  )
}

const ConsumeFolderDocumentation: React.FC<{ apiBaseUrl: string; currentLanguage: string }> = () => {
  const { t } = useTranslation()

  return (
    <div>
      <h2>{t('api_consume_folder')}</h2>
      <p className="lead">{t('api_consume_folder_description')}</p>

      <section className="mb-4">
        <h3>{t('api_consume_folder_what_is')}</h3>
        <p>{t('api_consume_folder_what_is_info')}</p>
        <p>{t('api_consume_folder_how_works')}</p>
      </section>

      <section className="mb-4">
        <h3>{t('api_consume_folder_configuration')}</h3>
        <p>{t('api_consume_folder_config_info')}</p>
        <div className="code-block">
          <pre>{`DOCDOT_CONSUMPTION_DIR=/path/to/consume/folder`}</pre>
        </div>
        <p>{t('api_consume_folder_config_note')}</p>
      </section>

      <section className="mb-4">
        <h3>{t('api_consume_folder_usage')}</h3>
        <p>{t('api_consume_folder_usage_info')}</p>
        <ul>
          <li>{t('api_consume_folder_usage_1')}</li>
          <li>{t('api_consume_folder_usage_2')}</li>
          <li>{t('api_consume_folder_usage_3')}</li>
          <li>{t('api_consume_folder_usage_4')}</li>
        </ul>
      </section>

      <section className="mb-4">
        <h3>{t('api_consume_folder_workflows')}</h3>
        <p>{t('api_consume_folder_workflows_info')}</p>
        <p>{t('api_consume_folder_workflows_filter_path')}</p>
        <div className="code-block">
          <pre>{`/invoices/*
/receipts/2024/*
*/urgent/*`}</pre>
        </div>
      </section>

      <section className="mb-4">
        <h3>{t('api_consume_folder_advanced')}</h3>
        <h4>{t('api_consume_folder_recursive')}</h4>
        <p>{t('api_consume_folder_recursive_info')}</p>
        <div className="code-block">
          <pre>{`DOCDOT_CONSUMER_RECURSIVE=true`}</pre>
        </div>

        <h4 className="mt-3">{t('api_consume_folder_subdirs_as_tags')}</h4>
        <p>{t('api_consume_folder_subdirs_as_tags_info')}</p>
        <div className="code-block">
          <pre>{`DOCDOT_CONSUMER_SUBDIRS_AS_TAGS=true
DOCDOT_CONSUMER_RECURSIVE=true`}</pre>
        </div>
        <p>{t('api_consume_folder_subdirs_as_tags_example')}</p>
      </section>

      <section className="mb-4">
        <h3>{t('api_consume_folder_important_notes')}</h3>
        <ul>
          <li>{t('api_consume_folder_note_1')}</li>
          <li>{t('api_consume_folder_note_2')}</li>
          <li>{t('api_consume_folder_note_3')}</li>
          <li>{t('api_consume_folder_note_4')}</li>
        </ul>
      </section>
    </div>
  )
}

const AuthenticationDocumentation: React.FC<{
  apiBaseUrl: string
  currentLanguage: string
  externalIntegrationsEnabled: boolean
}> = ({ apiBaseUrl, externalIntegrationsEnabled }) => {
  const { t } = useTranslation()
  const [authToken, setAuthToken] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [fetchingToken, setFetchingToken] = useState(false)
  const [tokenAvailable, setTokenAvailable] = useState(false)
  const [confirmGenerateOpen, setConfirmGenerateOpen] = useState(false)

  useEffect(() => {
    let isMounted = true

    if (!externalIntegrationsEnabled) {
      setAuthToken('')
      setTokenAvailable(false)
      return
    }
    setTokenAvailable(false)

    const loadTokenAvailability = async () => {
      try {
        const profile = await profileService.get()
        if (isMounted) {
          setTokenAvailable(Boolean(profile.has_auth_token))
        }
      } catch (error) {
        if (isMounted) {
          setTokenAvailable(false)
        }
      }
    }

    loadTokenAvailability()

    return () => {
      isMounted = false
    }
  }, [externalIntegrationsEnabled])

  const handleShowToken = async () => {
    if (!externalIntegrationsEnabled) {
      return
    }
    try {
      setFetchingToken(true)
      const response = await profileService.getAuthToken()
      setTokenAvailable(Boolean(response.hasToken))
      if (response.token) {
        setAuthToken(response.token)
      } else {
        setAuthToken('')
        toastService.showInfo(t('no_token_generated'))
      }
    } catch (error) {
      toastService.showError(t('error_loading_profile'))
    } finally {
      setFetchingToken(false)
    }
  }

  const handleGenerateTokenRequest = () => {
    if (!externalIntegrationsEnabled) {
      return
    }
    if (tokenAvailable && !authToken) {
      toastService.showInfo(t('fetch_token_first'))
      return
    }
    setConfirmGenerateOpen(true)
  }

  const handleGenerateToken = async () => {
    if (!externalIntegrationsEnabled) {
      setConfirmGenerateOpen(false)
      return
    }
    if (tokenAvailable && !authToken) {
      toastService.showInfo(t('fetch_token_first'))
      setConfirmGenerateOpen(false)
      return
    }
    try {
      setLoading(true)
      const newToken = await profileService.generateAuthToken()
      setAuthToken(newToken)
      setTokenAvailable(true)
      toastService.showSuccess(t('auth_token_generated'))
    } catch (error) {
      toastService.showError(t('error_generating_token'))
    } finally {
      setLoading(false)
      setConfirmGenerateOpen(false)
    }
  }

  const handleCopyToken = () => {
    if (!externalIntegrationsEnabled) return
    if (!authToken) return
    copyToClipboard(authToken)
    toastService.showSuccess(t('auth_token_copied'))
  }

  return (
    <div>
      <h2>{t('api_authentication')}</h2>
      <p className="lead">{t('api_auth_token_description')}</p>

      <section className="mb-4">
        <h3>{t('api_auth_token')}</h3>
        <p>{t('api_auth_token_info')}</p>

        <div className="api-token-section mb-4">
          <label htmlFor="auth_token" className="form-label fw-semibold">
            {t('api_auth_token')}
          </label>
          <div className="input-group">
            <input type="text" className="form-control" id="auth_token" value={authToken} readOnly placeholder={t('no_token_generated')} />
            {tokenAvailable && (
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={handleShowToken}
                title={t('show_token')}
                disabled={fetchingToken || !externalIntegrationsEnabled}
              >
                <i className={`bi ${fetchingToken ? 'bi-hourglass-split' : 'bi-eye'}`}></i>
              </button>
            )}
            <button
              className="btn btn-outline-secondary"
              type="button"
              onClick={handleCopyToken}
              title={t('copy_token')}
              disabled={!authToken || !externalIntegrationsEnabled}
            >
              <i className="bi bi-clipboard"></i>
            </button>
            <button
              className="btn btn-outline-secondary"
              type="button"
              onClick={handleGenerateTokenRequest}
              title={t('generate_new_token')}
              disabled={loading || !externalIntegrationsEnabled || (tokenAvailable && !authToken)}
            >
              <i className={`bi ${loading ? 'bi-hourglass-split' : 'bi-arrow-clockwise'}`}></i>
            </button>
          </div>
          <small className="form-text text-muted d-block mt-2">{t('token_change_warning')}</small>
        </div>

        <h4>{t('api_using_token')}</h4>
        <p>{t('api_auth_token_usage')}</p>
        <div className="code-block">
          <pre>{`Authorization: Token ${authToken || 'YOUR_TOKEN_HERE'}`}</pre>
        </div>

        <h4>{t('api_token_example')}</h4>
        <div className="code-block">
          <pre>{`curl -X GET "${apiBaseUrl}documents/" \\
  -H "Authorization: Token ${authToken || 'YOUR_TOKEN_HERE'}"`}</pre>
        </div>
      </section>
      <ConfirmDialog
        show={confirmGenerateOpen}
        title={t('generate_new_token_question')}
        message={t('generate_new_token_warning')}
        confirmText={t('generate_new_token')}
        variant="warning"
        onConfirm={handleGenerateToken}
        onCancel={() => setConfirmGenerateOpen(false)}
      />
    </div>
  )
}

const OrganizationsDocumentation: React.FC<{ apiBaseUrl: string; currentLanguage: string }> = ({ apiBaseUrl }) => {
  const { t } = useTranslation()

  return (
    <div>
      <h2>{t('api_organizations')}</h2>
      <p className="lead">{t('api_organizations_description')}</p>

      <section className="mb-4">
        <h3>{t('api_organizations_context')}</h3>
        <p>{t('api_organizations_context_info')}</p>
      </section>

      <section className="mb-4">
        <h3>{t('api_organizations_header')}</h3>
        <p>{t('api_organizations_header_info')}</p>
        <div className="code-block">
          <pre>{`curl -X GET "${apiBaseUrl}documents/" \\
  -H "Authorization: Token YOUR_API_TOKEN" \\
  -H "X-Organization-Id: 2"`}</pre>
        </div>
      </section>

      <section className="mb-4">
        <h3>{t('api_organizations_query')}</h3>
        <p>{t('api_organizations_query_info')}</p>
        <div className="code-block">
          <pre>{`curl -X GET "${apiBaseUrl}documents/?organization_id=2" \\
  -H "Authorization: Token YOUR_API_TOKEN"`}</pre>
        </div>
      </section>

      <section className="mb-4">
        <h3>{t('api_organizations_default')}</h3>
        <p>{t('api_organizations_default_info')}</p>
        <div className="code-block">
          <pre>{`curl -X GET "${apiBaseUrl}documents/" \\
  -H "Authorization: Token YOUR_API_TOKEN"`}</pre>
        </div>
      </section>

      <section className="mb-4">
        <h3>{t('api_organizations_list')}</h3>
        <p>{t('api_organizations_list_info')}</p>
        <div className="code-block">
          <pre>{`curl -X GET "${apiBaseUrl}ui_settings/" \\
  -H "Authorization: Token YOUR_API_TOKEN"`}</pre>
        </div>
        <div className="code-block">
          <pre>{`{
  "currentOrganizationId": 2,
  "organizations": [
    { "id": 1, "name": "Organizacija A", "is_default": true },
    { "id": 2, "name": "Organizacija B", "is_default": false }
  ]
}`}</pre>
        </div>
      </section>

      <section className="mb-4">
        <h3>{t('api_organizations_errors')}</h3>
        <p>{t('api_organizations_errors_info')}</p>
        <div className="table-responsive">
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>HTTP</th>
                <th>
                  <code>code</code>
                </th>
                <th>{t('api_field_description')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>400</td>
                <td>
                  <code>organization_not_selected</code>
                </td>
                <td>{t('api_organizations_error_not_selected')}</td>
              </tr>
              <tr>
                <td>403</td>
                <td>
                  <code>organization_not_accessible</code>
                </td>
                <td>{t('api_organizations_error_not_accessible')}</td>
              </tr>
              <tr>
                <td>400</td>
                <td>
                  <code>invalid_organization_id</code>
                </td>
                <td>{t('api_organizations_error_invalid')}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="code-block">
          <pre>{`HTTP 400 Bad Request
{
  "detail": "No organization selected. Pass the X-Organization-Id header or set a default organization for this user.",
  "code": "organization_not_selected",
  "organizations": [
    { "id": 1, "name": "Organizacija A", "is_default": false },
    { "id": 2, "name": "Organizacija B", "is_default": false }
  ]
}`}</pre>
        </div>
      </section>

      <section className="mb-4">
        <h3>{t('api_organizations_notes')}</h3>
        <ul>
          <li>{t('api_organizations_note_permissions')}</li>
          <li>{t('api_organizations_note_write')}</li>
          <li>{t('api_organizations_note_session')}</li>
          <li>{t('api_organizations_note_cors')}</li>
        </ul>
      </section>
    </div>
  )
}

const DocumentsDocumentation: React.FC<{ apiBaseUrl: string; currentLanguage: string }> = ({ apiBaseUrl }) => {
  const { t } = useTranslation()

  return (
    <div>
      <h2>{t('api_documents')}</h2>
      <p className="lead">{t('api_documents_description')}</p>

      <section className="mb-4">
        <h3>GET {t('api_list_documents')}</h3>
        <div className="code-block">
          <code>GET {apiBaseUrl}documents/</code>
        </div>
        <p>{t('api_documents_list_info')}</p>
        <div className="code-block">
          <pre>{`GET ${apiBaseUrl}documents/?page=1&page_size=25&ordering=-created`}</pre>
        </div>
      </section>

      <section className="mb-4">
        <h3>GET {t('api_document_detail')}</h3>
        <div className="code-block">
          <code>
            GET {apiBaseUrl}documents/{'{id}'}/
          </code>
        </div>
        <p>{t('api_document_detail_info')}</p>
      </section>

      <section className="mb-4">
        <h3>GET {t('api_preview_document')}</h3>
        <div className="code-block">
          <code>
            GET {apiBaseUrl}documents/{'{id}'}/preview/
          </code>
        </div>
        <p>{t('api_preview_document_info')}</p>
        <div className="code-block">
          <pre>{`GET ${apiBaseUrl}documents/{id}/preview/?original=true`}</pre>
        </div>
      </section>

      <section className="mb-4">
        <h3>PATCH {t('api_update_document')}</h3>
        <div className="code-block">
          <code>
            PATCH {apiBaseUrl}documents/{'{id}'}/
          </code>
        </div>
        <p>{t('api_update_document_info')}</p>
      </section>

      <section className="mb-4">
        <h3>DELETE {t('api_delete_document')}</h3>
        <div className="code-block">
          <code>
            DELETE {apiBaseUrl}documents/{'{id}'}/
          </code>
        </div>
        <p>{t('api_delete_document_info')}</p>
      </section>
    </div>
  )
}

const SearchDocumentation: React.FC<{ apiBaseUrl: string; currentLanguage: string }> = ({ apiBaseUrl }) => {
  const { t } = useTranslation()

  return (
    <div>
      <h2>{t('api_search_documents')}</h2>
      <p className="lead">{t('api_search_description')}</p>

      <section className="mb-4">
        <h3>{t('api_full_text_search')}</h3>
        <div className="code-block">
          <code>GET {apiBaseUrl}documents/?query=search+term</code>
        </div>
        <p>{t('api_search_info')}</p>
        <p>{t('api_search_syntax_note')}</p>
      </section>

      <section className="mb-4">
        <h3>{t('api_similar_documents')}</h3>
        <div className="code-block">
          <code>GET {apiBaseUrl}documents/?more_like_id=1234</code>
        </div>
        <p>{t('api_similar_documents_info')}</p>
      </section>

      <section className="mb-4">
        <h3>{t('api_search_response')}</h3>
        <p>{t('api_search_response_info')}</p>
        <div className="code-block">
          <pre>{`{
  "count": 31,
  "next": "${apiBaseUrl}documents/?page=2&query=test",
  "previous": null,
  "results": [
    {
      "id": 123,
      "title": "title",
      "content": "content",
      "__search_hit__": {
        "score": 0.343,
        "highlights": "text <span class="match">Test</span> text",
        "rank": 23
      }
    }
  ]
}`}</pre>
        </div>
        <p>{t('api_search_hit_attributes')}</p>
        <ul>
          <li>
            <code>score</code>: {t('api_search_score_desc')}
          </li>
          <li>
            <code>highlights</code>: {t('api_search_highlights_desc')}
          </li>
          <li>
            <code>rank</code>: {t('api_search_rank_desc')}
          </li>
        </ul>
      </section>

      <section className="mb-4">
        <h3>{t('api_filtering')}</h3>
        <p>{t('api_filtering_info')}</p>

        <h4>{t('api_filter_by_tags')}</h4>
        <div className="code-block">
          <pre>{`GET ${apiBaseUrl}documents/?tags__id__in=1,2,3
GET ${apiBaseUrl}documents/?tags__id__all=1,2
GET ${apiBaseUrl}documents/?tags__id__none=5
GET ${apiBaseUrl}documents/?is_tagged=true`}</pre>
        </div>

        <h4>{t('api_filter_by_correspondent')}</h4>
        <div className="code-block">
          <pre>{`GET ${apiBaseUrl}documents/?correspondent__id=1
GET ${apiBaseUrl}documents/?correspondent__id__in=1,2,3
GET ${apiBaseUrl}documents/?correspondent__id__none=5`}</pre>
        </div>

        <h4>{t('api_filter_by_document_type')}</h4>
        <div className="code-block">
          <pre>{`GET ${apiBaseUrl}documents/?document_type__id=1
GET ${apiBaseUrl}documents/?document_type__id__in=1,2,3
GET ${apiBaseUrl}documents/?document_type__id__none=5`}</pre>
        </div>

        <h4>{t('api_filter_by_storage_path')}</h4>
        <div className="code-block">
          <pre>{`GET ${apiBaseUrl}documents/?storage_path__id=1
GET ${apiBaseUrl}documents/?storage_path__id__in=1,2,3
GET ${apiBaseUrl}documents/?storage_path__id__none=5`}</pre>
        </div>

        <h4>{t('api_filter_by_dates')}</h4>
        <div className="code-block">
          <pre>{`GET ${apiBaseUrl}documents/?created__date__gte=2024-01-01
GET ${apiBaseUrl}documents/?created__date__lte=2024-12-31
GET ${apiBaseUrl}documents/?added__date__gte=2024-01-01
GET ${apiBaseUrl}documents/?added__date__lte=2024-12-31`}</pre>
        </div>

        <h4>{t('api_filter_by_title')}</h4>
        <div className="code-block">
          <pre>{`GET ${apiBaseUrl}documents/?title__icontains=invoice`}</pre>
        </div>

        <h4>{t('api_filter_by_asn')}</h4>
        <div className="code-block">
          <pre>{`GET ${apiBaseUrl}documents/?archive_serial_number=123
GET ${apiBaseUrl}documents/?archive_serial_number__gt=100
GET ${apiBaseUrl}documents/?archive_serial_number__lt=200
GET ${apiBaseUrl}documents/?archive_serial_number__isnull=true`}</pre>
        </div>

        <h4>{t('api_filter_by_mime_type')}</h4>
        <div className="code-block">
          <pre>{`GET ${apiBaseUrl}documents/?mime_type=application/pdf`}</pre>
        </div>
      </section>

      <section className="mb-4">
        <h3>{t('api_custom_fields_filter')}</h3>
        <p>{t('api_custom_fields_filter_info')}</p>
        <div className="code-block">
          <pre>{`GET ${apiBaseUrl}documents/?custom_field_query=["due", "range", ["2024-08-01", "2024-09-01"]]
GET ${apiBaseUrl}documents/?custom_field_query=["customer", "exact", "bob"]
GET ${apiBaseUrl}documents/?custom_field_query=["answered", "exact", true]
GET ${apiBaseUrl}documents/?custom_field_query=["favorite animal", "in", ["cat", "dog"]]
GET ${apiBaseUrl}documents/?custom_field_query=["address", "isnull", true]
GET ${apiBaseUrl}documents/?custom_field_query=["foo", "exists", false]`}</pre>
        </div>
        <p>{t('api_custom_fields_operations')}</p>
        <ul>
          <li>{t('api_custom_fields_basic')}</li>
          <li>{t('api_custom_fields_string')}</li>
          <li>{t('api_custom_fields_numeric')}</li>
          <li>{t('api_custom_fields_document_link')}</li>
        </ul>
      </section>

      <section className="mb-4">
        <h3>{t('api_pagination')}</h3>
        <p>{t('api_pagination_info')}</p>
        <div className="code-block">
          <pre>{`GET ${apiBaseUrl}documents/?page=1&page_size=25`}</pre>
        </div>
      </section>

      <section className="mb-4">
        <h3>{t('api_ordering')}</h3>
        <p>{t('api_ordering_info')}</p>
        <div className="code-block">
          <pre>{`GET ${apiBaseUrl}documents/?ordering=-created
GET ${apiBaseUrl}documents/?ordering=title
GET ${apiBaseUrl}documents/?ordering=archive_serial_number`}</pre>
        </div>
        <p>{t('api_ordering_note')}</p>
      </section>

      <section className="mb-4">
        <h3>{t('api_autocomplete')}</h3>
        <div className="code-block">
          <code>GET {apiBaseUrl}search/autocomplete/?term=partia&limit=10</code>
        </div>
        <p>{t('api_autocomplete_info')}</p>
        <div className="code-block">
          <pre>{`["term1", "term3", "term6", "term4"]`}</pre>
        </div>
      </section>
    </div>
  )
}

const BulkEditDocumentation: React.FC<{ apiBaseUrl: string; currentLanguage: string }> = ({ apiBaseUrl }) => {
  const { t } = useTranslation()

  return (
    <div>
      <h2>{t('api_bulk_edit')}</h2>
      <p className="lead">{t('api_bulk_edit_description')}</p>

      <section className="mb-4">
        <h3>{t('api_endpoint')}</h3>
        <div className="code-block">
          <code>POST {apiBaseUrl}documents/bulk_edit/</code>
        </div>
      </section>

      <section className="mb-4">
        <h3>{t('api_request_example')}</h3>
        <div className="code-block">
          <pre>{`POST ${apiBaseUrl}documents/bulk_edit/
Content-Type: application/json

{
  "documents": [1, 2, 3],
  "method": "set_correspondent",
  "parameters": {
    "correspondent": 5
  }
}`}</pre>
        </div>
      </section>

      <section className="mb-4">
        <h3>{t('api_available_methods')}</h3>
        <ul>
          <li>
            <code>set_correspondent</code>
          </li>
          <li>
            <code>set_document_type</code>
          </li>
          <li>
            <code>set_storage_path</code>
          </li>
          <li>
            <code>add_tag</code>
          </li>
          <li>
            <code>remove_tag</code>
          </li>
          <li>
            <code>delete</code>
          </li>
        </ul>
      </section>
    </div>
  )
}

const TagsDocumentation: React.FC<{ apiBaseUrl: string; currentLanguage: string }> = ({ apiBaseUrl }) => {
  const { t } = useTranslation()

  return (
    <div>
      <h2>{t('api_tags')}</h2>
      <p className="lead">{t('api_tags_description')}</p>

      <section className="mb-4">
        <h3>GET {t('api_list_tags')}</h3>
        <div className="code-block">
          <code>GET {apiBaseUrl}tags/</code>
        </div>
      </section>

      <section className="mb-4">
        <h3>POST {t('api_create_tag')}</h3>
        <div className="code-block">
          <code>POST {apiBaseUrl}tags/</code>
        </div>
      </section>

      <section className="mb-4">
        <h3>PATCH {t('api_update_tag')}</h3>
        <div className="code-block">
          <code>
            PATCH {apiBaseUrl}tags/{'{id}'}/
          </code>
        </div>
      </section>

      <section className="mb-4">
        <h3>DELETE {t('api_delete_tag')}</h3>
        <div className="code-block">
          <code>
            DELETE {apiBaseUrl}tags/{'{id}'}/
          </code>
        </div>
      </section>
    </div>
  )
}

const CorrespondentsDocumentation: React.FC<{ apiBaseUrl: string; currentLanguage: string }> = ({ apiBaseUrl }) => {
  const { t } = useTranslation()

  return (
    <div>
      <h2>{t('api_correspondents')}</h2>
      <p className="lead">{t('api_correspondents_description')}</p>

      <section className="mb-4">
        <h3>GET {t('api_list_correspondents')}</h3>
        <div className="code-block">
          <code>GET {apiBaseUrl}correspondents/</code>
        </div>
      </section>

      <section className="mb-4">
        <h3>POST {t('api_create_correspondent')}</h3>
        <div className="code-block">
          <code>POST {apiBaseUrl}correspondents/</code>
        </div>
      </section>

      <section className="mb-4">
        <h3>PATCH {t('api_update_correspondent')}</h3>
        <div className="code-block">
          <code>
            PATCH {apiBaseUrl}correspondents/{'{id}'}/
          </code>
        </div>
      </section>

      <section className="mb-4">
        <h3>DELETE {t('api_delete_correspondent')}</h3>
        <div className="code-block">
          <code>
            DELETE {apiBaseUrl}correspondents/{'{id}'}/
          </code>
        </div>
      </section>
    </div>
  )
}

const DocumentTypesDocumentation: React.FC<{ apiBaseUrl: string; currentLanguage: string }> = ({ apiBaseUrl }) => {
  const { t } = useTranslation()

  return (
    <div>
      <h2>{t('api_document_types')}</h2>
      <p className="lead">{t('api_document_types_description')}</p>

      <section className="mb-4">
        <h3>GET {t('api_list_document_types')}</h3>
        <div className="code-block">
          <code>GET {apiBaseUrl}document_types/</code>
        </div>
      </section>

      <section className="mb-4">
        <h3>POST {t('api_create_document_type')}</h3>
        <div className="code-block">
          <code>POST {apiBaseUrl}document_types/</code>
        </div>
      </section>

      <section className="mb-4">
        <h3>PATCH {t('api_update_document_type')}</h3>
        <div className="code-block">
          <code>
            PATCH {apiBaseUrl}document_types/{'{id}'}/
          </code>
        </div>
      </section>

      <section className="mb-4">
        <h3>DELETE {t('api_delete_document_type')}</h3>
        <div className="code-block">
          <code>
            DELETE {apiBaseUrl}document_types/{'{id}'}/
          </code>
        </div>
      </section>
    </div>
  )
}

const StoragePathsDocumentation: React.FC<{ apiBaseUrl: string; currentLanguage: string }> = ({ apiBaseUrl }) => {
  const { t } = useTranslation()

  return (
    <div>
      <h2>{t('api_storage_paths')}</h2>
      <p className="lead">{t('api_storage_paths_description')}</p>

      <section className="mb-4">
        <h3>GET {t('api_list_storage_paths')}</h3>
        <div className="code-block">
          <code>GET {apiBaseUrl}storage_paths/</code>
        </div>
      </section>

      <section className="mb-4">
        <h3>POST {t('api_create_storage_path')}</h3>
        <div className="code-block">
          <code>POST {apiBaseUrl}storage_paths/</code>
        </div>
      </section>

      <section className="mb-4">
        <h3>PATCH {t('api_update_storage_path')}</h3>
        <div className="code-block">
          <code>
            PATCH {apiBaseUrl}storage_paths/{'{id}'}/
          </code>
        </div>
      </section>

      <section className="mb-4">
        <h3>DELETE {t('api_delete_storage_path')}</h3>
        <div className="code-block">
          <code>
            DELETE {apiBaseUrl}storage_paths/{'{id}'}/
          </code>
        </div>
      </section>
    </div>
  )
}
